import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  MonetizationOn as MoneyIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { setInitialCost } from '../../services/maintenanceCostService';
import { formatCurrency } from '../../utils/numberUtils';

interface InitialCostDialogProps {
  open: boolean;
  onClose: () => void;
  maintenanceId: string;
  customerName: string;
  deviceType: string;
  userId: string;
  onSuccess: () => void;
}

const InitialCostDialog: React.FC<InitialCostDialogProps> = ({
  open,
  onClose,
  maintenanceId,
  customerName,
  deviceType,
  userId,
  onSuccess
}) => {
  const [cost, setCost] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (cost <= 0) {
      setError('يجب إدخال تكلفة أكبر من صفر');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await setInitialCost(maintenanceId, cost, userId);
      onSuccess();
      onClose();
      setCost(0);
    } catch (error) {
      console.error('Error setting initial cost:', error);
      setError('حدث خطأ أثناء تسجيل التكلفة المبدئية');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setCost(0);
      setError(null);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ReceiptIcon color="primary" />
          <Typography variant="h6">
            تحديد التكلفة المبدئية
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box py={1}>
          {/* معلومات الطلب */}
          <Box mb={3} p={2} bgcolor="background.default" borderRadius={1}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              معلومات الطلب
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>العميل:</strong> {customerName}
            </Typography>
            <Typography variant="body2">
              <strong>نوع الجهاز:</strong> {deviceType}
            </Typography>
          </Box>

          {/* إدخال التكلفة */}
          <TextField
            label="التكلفة المبدئية"
            type="number"
            fullWidth
            value={cost || ''}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              setCost(Math.max(0, value));
              setError(null);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MoneyIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">جنيه</InputAdornment>,
              inputProps: { min: 0, step: 0.01 }
            }}
            helperText="التكلفة المقدرة للصيانة قبل الفحص التفصيلي"
            error={!!error}
            disabled={loading}
            autoFocus
          />

          {/* عرض التكلفة المنسقة */}
          {cost > 0 && (
            <Box mt={2} p={2} bgcolor="success.light" borderRadius={1}>
              <Typography variant="body2" color="success.dark">
                <strong>التكلفة المبدئية:</strong> {formatCurrency(cost)}
              </Typography>
            </Box>
          )}

          {/* رسالة خطأ */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* معلومات إضافية */}
          <Box mt={3} p={2} bgcolor="info.light" borderRadius={1}>
            <Typography variant="caption" color="info.dark">
              💡 <strong>ملاحظة:</strong> يمكن تعديل هذه التكلفة لاحقاً بعد الفحص التفصيلي للجهاز
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || cost <= 0}
          startIcon={loading ? <CircularProgress size={20} /> : <MoneyIcon />}
        >
          {loading ? 'جاري الحفظ...' : 'تسجيل التكلفة'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InitialCostDialog;
